<template>
  <div class="card card-shadow p-2 mb-2">
    <div class="columns pl-2 pr-2 pt-1 pb-0">
      <div class="column is-3 ml-2">
        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Site name</div>
        <div class="has-text-black has-text-weight-bold">{{ siteData.name }}</div>
      </div>
      <div class="column m-0" style="width: 15%; max-width: 15%">
        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Devices</div>
        <div class="has-text-black has-text-weight-bold">{{ siteData ? siteData.devices.map(dev => dev.name).toString() : '' }}</div>
      </div>
      <hr class="mt-3 mb-4 has-background-grey-lighter mr-0" style="width: 1px; height: 48px">
      <div class="column m-0 ml-2 pl-1 pr-1" style="width: 110px; max-width: 110px">
        <div class="has-text-centered clickable-info p-1" style="height: 48px">
          <div class="is-size-7 has-text-grey has-text-weight-normal mb-1" style="line-height: 1">Versions</div>
          <div v-for="version in getVersions()" :key="version" class="is-size-7" style="line-height: 1">
            {{version}}
          </div>
        </div>
      </div>
      <div class="column m-0 ml-2 pl-1 pr-1" style="width: 110px; max-width: 110px" @click="onUpdateStageNames()">
        <div class="has-text-centered p-1" style="height: 48px;" :class="[siteData.devices.length > 1 ? 'clickable-info-editable' : 'clickable-info']">
          <div class="is-size-7 has-text-grey has-text-weight-normal mb-1" style="line-height: 1">Selected Stages</div>
          <div v-for="stageName in getSelectedStageNames()" :key="stageName" class="is-size-7" style="line-height: 1">
            {{stageName}}
          </div>
        </div>
      </div>
      <hr class="mt-3 mb-4 has-background-grey-lighter ml-2" style="width: 1px; height: 48px">
      <div class="column m-0 ml-2 pl-1 pr-1" style="width: 130px; max-width: 130px" v-if="status">
        <div class="has-text-centered clickable-info p-1" :style="{background: status.color}">
          <div class="is-size-7 has-text-white pt-3 pb-3">{{ status.text }}</div>
        </div>
      </div>
      <hr class="mt-3 mb-4 has-background-grey-lighter ml-2" style="width: 1px; height: 48px">
      <div class="column is-flex" style="width: 100%" v-if="status">
        <div style="height: 48px; width: 100%"
             class="is-flex is-align-items-center dashed-border-small ml-0 mr-3 mt-auto mb-auto">
          <div v-for="message in status.messages" :key="message.msg" class="is-size-7 ml-auto mr-auto has-text-weight-light has-text-centered"
          :style="{color: message.color}" style="line-height: 1">
            <b-tooltip v-if="message.note" :label="message.note" :delay="1000" type="is-dark" style="cursor: pointer"
                       position="is-top">
              <div style="white-space: pre-line;">{{message.msg}}</div>
            </b-tooltip>
            <div style="white-space: pre-line;" v-else>{{message.msg}}</div>
          </div>
        </div>
        <div v-if="isLocked" class="is-flex ml-0 mt-auto mb-auto mr-0">
          <b-tooltip v-if="status.actions.includes('download')" label="Download Data" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="download-circle"
                class="clickable" size="is-medium" style="color: #18AA7E"
                @click.native="onDownloadClick"></b-icon>
          </b-tooltip>
          <b-tooltip :label="lockLabel" :delay="500" type="is-warning" position="is-left" class="mr-1">
            <b-icon icon="lock" size="is-medium" style="color: rgba(0,0,0,0.2); cursor: not-allowed"></b-icon>
          </b-tooltip>
        </div>
        <div v-else v-for="action in status.actions" :key="action" class="is-flex ml-0 mt-auto mb-auto mr-0">
          <b-tooltip v-if="action === 'repackage'" label="Repackage Data" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="play-circle"
                class="clickable" size="is-medium" style="color: #626262"
                @click.native="onPrepareClick"></b-icon>
          </b-tooltip>
          <b-tooltip v-if="action === 'download'" label="Download Data" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="download-circle"
                class="clickable" size="is-medium" style="color: #18AA7E"
                @click.native="onDownloadClick"></b-icon>
          </b-tooltip>

          <b-tooltip v-if="action === 'submit'" label="Submit Data" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="send-circle"
                class="clickable" size="is-medium" style="color: #18AA7E"
                @click.native="onSubmitData"></b-icon>
          </b-tooltip>

          <b-tooltip v-if="action === 'submit_response'" label="Submit Response" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="send-circle"
                class="clickable" size="is-medium" style="color: #18AA7E"
                @click.native="onSubmitResponse"></b-icon>
          </b-tooltip>
          <b-tooltip v-if="action === 'extract'" label="Extract Images" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="image-move"
                class="clickable" size="is-medium" style="color: #626262"
                @click.native="onExtractImages"></b-icon>
          </b-tooltip>
          <b-tooltip v-if="action === 'wait'"
                     class="ml-auto mr-auto mt-auto mb-auto" label="Please Wait..." :delay="1000" type="is-dark" position="is-left">
            <b-icon
                icon="progress-clock"
                class="clickable" size="is-medium"></b-icon>

            <b-progress size="is-small" :value="progress"></b-progress>
          </b-tooltip>
          <b-tooltip v-if="action === 'package'" label="Prepare package" :delay="1000" type="is-dark" class="mr-1"
                     position="is-left">
            <b-icon
                icon="play-circle"
                class="clickable" size="is-medium" style="color: #626262"
                @click.native="onPrepareClick"></b-icon>
          </b-tooltip>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {ManualCorrectionStatus, getManualCorrectionLabel} from "@/utils/utils";

export default {
  name: "packaging-data-card",
  components: {},
  props: {
    siteData: {
      type: Object
    },
    progress: {
      type: Number
    },
  },
  data() {
    return {
      status: null,
    }
  },
  mounted() {
    this.getStatus()

  },
  methods: {
    ...mapActions({
      setSelectedPackagingDeviceDevice: 'dataPackagingModule/setSelectedPackagingDevice'
    }),
    onPrepareClick() {
      this.$emit('onPrepareClick', this.siteData)
    },
    onDownloadClick() {
      this.$emit('onDownloadClick', this.siteData)
    },
    onSubmitData() {
      console.log('On submit data clicked')
      this.$emit('onSubmitData', this.siteData._id)
    },
    onSubmitResponse(){
      console.log('On submit response clicked')
      this.$emit('onSubmitResponse', this.siteData._id)
    },
    onExtractImages(){
      console.log('On extract images clicked')
      this.$emit('onExtractImages', this.siteData._id)
    },
    onUpdateStageNames(){
      if(this.siteData.devices && this.siteData.devices.length > 1)
      this.$emit('onUpdateStageNames', this.siteData)
    },
    onShowError(errors) {
      this.$emit('onShowError', errors)
    },
    showToast(message, type) {
      this.$buefy.toast.open({
        message: message,
        duration: 3000,
        type: type
      })
    },
    getVersions(){
      let versions = []
      if(this.siteData && this.siteData.devices.length > 0){
        this.siteData.devices.forEach(device => {
          let version = `${device.name}:`
          if(device.packagedDataset){
            version += `${device.packagedDataset.version}`
          }else{
            version += `N/A`
          }
          versions.push(version)
        })
      }

      return versions
    },
    getSelectedStageNames(){
      if(this.siteData && this.siteData.surveyDetails && this.siteData.surveyDetails.selectedStageNames && this.siteData.surveyDetails.selectedStageNames.length > 0){
        return this.siteData.surveyDetails.selectedStageNames
      }
      return ["Default"]
    },
    getStatus() {
      if(this.siteData){
        if(this.siteData.status === 'packaged'){
          this.status = {
            actions: ['repackage','download','submit'],
            text: 'DATA PACKAGED',
            color: '#0C6051',
            messages: [{msg: `Site data prepared for downloading/submitting`, color: '#18AA7E'}]
          }
        }else if(this.siteData.status === 'submitted'){
          let additionalMessages = []
          if(this.siteData.surveyDetails && this.siteData.surveyDetails.submissionStatus ){
            additionalMessages.push({msg: `Submission status: \n${this.siteData.surveyDetails.submissionStatus}`, color: 'rgba(0,0,0,0.6)'})
          }
          if(this.siteData.surveyDetails && this.siteData.surveyDetails.submissionVerification){
            if(this.siteData.surveyDetails.suppliedDate){
              additionalMessages.push({msg: `Submitted at: \n${new Date(this.siteData.surveyDetails.suppliedDate).toLocaleString()}`, color: 'rgba(0,0,0,0.6)'})
            }
            if(this.siteData.surveyDetails.submissionVerification.suppliedDate){
              console.log('Supplied date: ' + this.siteData.surveyDetails.submissionVerification.suppliedDate)
              additionalMessages.push({msg: `Received at: \n${new Date(this.siteData.surveyDetails.submissionVerification.suppliedDate).toLocaleString()}`, color: 'rgba(0,0,0,0.6)'})
            }

            if(this.siteData.surveyDetails.submissionVerification.expectedAttachmentCount){
              additionalMessages.push({msg: `Expected Count: \n${this.siteData.surveyDetails.submissionVerification.expectedAttachmentCount}`, color: 'rgba(0,0,0,0.6)'})
            }

            if(this.siteData.surveyDetails.submissionVerification.arcgisAttachmentCount){
              additionalMessages.push({msg: `Arcgis Count: \n${this.siteData.surveyDetails.submissionVerification.arcgisAttachmentCount}`, color: 'rgba(0,0,0,0.6)'})
            }
          }
          this.status = {
            actions: ['repackage','download'],
            text: 'DATA SUBMITTED',
            color: '#18AA7E',
            messages: [{msg: `Site data:\nsubmitted`, color: 'rgba(0,0,0,0.6)'}, ...additionalMessages]
          }
        }else if(this.siteData.status === 'dtpQuery'){
          this.status = {
            actions: ['submit_response'],
            text: 'DTP QUERY',
            color: '#ED9822',
            messages: [{msg: `There is Dtp Query. Submit response.`, color: '#ff0000'}]
          }
        }else if(this.siteData.status === 'responded'){
          this.status = {
            actions: [],
            text: 'QUERY SUBMITTED',
            color: '#ED9822',
            messages: [{msg: `There is Dtp Query. Response is submitted.`, color: 'rgba(0,0,0,0.6)'}]
          }
        }else{
          if(this.siteData.devices.length > 0){

            let isDataApproved = true
            let dataApprovedMessages = []
            let isImagesExtracted = true
            let isImagesInProgress = false
            let imagesMessages = []

            this.siteData.devices.forEach(device => {

              if (device.processingStatus && device.processingStatus === 'DataApproved') {
                dataApprovedMessages.push({msg: `${device.name}:Data Approved`, color: '#18AA7E'})
              }else if(device.processingStatus && device.processingStatus === 'ImagesExtracted'){
                dataApprovedMessages.push({msg: `${device.name}:Images Extracted`, color: '#18AA7E'})
              }else {
                isDataApproved = false
                dataApprovedMessages.push({msg: `${device.name}:Data Not Approved`, color: 'red'})
              }


              if(device.tasks){
                if(device.tasks.lifecycleStatus === 'completed'){
                  imagesMessages.push({msg: `${device.name}:Extract Images DONE`, color: '#18AA7E'})
                }else if(device.tasks.lifecycleStatus === 'failed'){
                  isImagesExtracted = false
                  imagesMessages.push({msg: `${device.name}:Extract Images FAILED`, color: 'red', note: device.tasks.note})
                }else{
                  isImagesExtracted = false
                  isImagesInProgress = true
                  imagesMessages.push({msg: `${device.name}:Extracting Images...`, color: '#ED9822', note: device.tasks.note})
                }
              }else{
                isImagesExtracted = false
                imagesMessages.push({msg: `${device.name}:Images Not Extracted`, color: 'red'})
              }
            })


            if(isDataApproved && isImagesExtracted){
              this.status = {
                actions: ['package'],
                text: 'READY',
                color: '#8C84FF',
                messages: [...dataApprovedMessages, ...imagesMessages]
              }
            }else if(isDataApproved && isImagesInProgress){
              this.status = {
                actions: ['wait'],
                text: 'EXTRACTING IMAGES',
                color: 'orange',
                messages: [...dataApprovedMessages, ...imagesMessages]
              }
              this.$emit('onRefreshActive')
            }else if(isDataApproved && !isImagesInProgress && !isImagesExtracted){
              this.status = {
                actions: ['extract'],
                text: 'MISSING IMAGES',
                color: 'red',
                messages: [...dataApprovedMessages, ...imagesMessages]
              }
            }else if(!isDataApproved){
              this.status = {
                actions: [],
                text: 'NOT READY',
                color: 'black',
                messages: [...dataApprovedMessages, ...imagesMessages]
              }
            }
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      selectedPackagingProject: 'dataPackagingModule/getSelectedPackagingProject',
      selectedDevice: 'dataPackagingModule/getSelectedPackagingDevice'
    }),
    isLocked() {
      if (this.siteData && this.siteData.devices && this.siteData.devices.length > 0) {
        return this.siteData.devices.some(device => device.manualCorrection && device.manualCorrection.status !== ManualCorrectionStatus.EVENTS_VALIDATED.value)
      }
      return false
    },
    lockLabel() {
      if (this.siteData && this.siteData.devices && this.siteData.devices.length > 0) {
        return this.siteData.devices
            .filter(device => device.manualCorrection && device.manualCorrection.status !== ManualCorrectionStatus.EVENTS_VALIDATED.value)
            .map(device => device.name + ' - ' + getManualCorrectionLabel(device.manualCorrection.status))
            .join(', ')
      }
      return ''
    },
  },
  watch: {
    /*    progress: {
          handler: function (after, before) {
            this.setProgress(after)
          },
          deep: true
        },
        progressStepOne: {
          handler: function (after, before) {
            this.setProgressStepOne(after)
          },
          deep: true
        },
        refresh: {
          handler: function (after, before) {
            //this.setRefreshing(after)
          },
          deep: true
        }*/
    siteData: {
      handler: function (after, before) {
        console.log('On site data changed')
        this.getStatus()
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.card-shadow {
  box-shadow: 0 0.125em 0.125em -0.125em rgb(10 10 10 / 10%), 0 0px 0 1px rgb(10 10 10 / 2%);
  background-color: white;
  border-radius: 0.35rem;
}

.clickable-info {
  opacity: 0.5;
  background: #f4f4f4;
  border-radius: 0.3rem;
}

.clickable-info-editable {
  background: #f4f4f4;
  border-radius: 0.3rem;
}

.clickable-info-editable:hover {
  cursor: pointer;
  box-shadow: 0 0.125em 0.125em -0.125em rgb(10 10 10 / 20%), 0 0px 0 1px rgb(10 10 10 / 5%);
  background: #e7e7e7;
}
.dashed-border-small {
  padding: 8px 8px;
  border-style: dashed;
  border-radius: 8px;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.1);
}
</style>